/** @jsxImportSource @emotion/react */
import { Button, Input, ProgressCircle, useToast } from "@coreai/component-library";
import { css, useTheme } from "@emotion/react";
import  { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createProject } from "../../../services/projectsService";
import { useLanguage } from "LanguageContext";
import AdvertiserSearch from "./AdvertiserSearch";

interface NewProjectProps {
  setNewProject: (set: boolean) => void;
}

export default function NewProject(props: NewProjectProps) {
  const { t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  const [advertiserName, setAdvertiserName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null); // Explicitly type the ref

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    colors: ${theme.typography.heading3_medium.colors};
  `;  
  const setFocuseAdver = () => {
    if (inputRef.current) {
      inputRef.current.focus();    
    }
  };
  const cardStyles = css`
    display: flex;
    flex-direction: column;
    background-color: ${theme.colors.white};
    padding: ${theme.spacing["4xl"]};
    box-shadow: ${theme.shadows.shallow};
    border-radius: ${theme.radius.xs};
    margin-top: ${theme.spacing["5xl"]};
    margin-bottom: ${theme.spacing.xxl};
  `;

  const compulsoryStyles = css`
    font-family: ${theme.typography.subbody_medium.fontFamily};
    font-size: ${theme.typography.subbody_medium.fontSize};
    font-weight: ${theme.typography.subbody_medium.fontWeight};
    color: ${theme.typography.subbody_medium.colors};
  `;

  const formStyles = css`
    display: flex;
    flex-direction: column;
    margin-top: ${theme.spacing.xl};
    gap: ${theme.spacing.xl};
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: ${theme.typography.bodyMedium.fontWeight};
    colors: ${theme.colors.purple};
  `;

  const inputRowStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing["4xl"]};
    > div {
      flex: 1;
      min-width: 200px;
    }
  `;

  const buttonContainerStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-top: ${theme.spacing.xl};

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;

      > button {
        align-self: flex-end;
      }
    }
  `;

  const onCreateProject = () => {
    setIsLoading(true);
    createProject({ advertiser: advertiserName, name: projectName })
      .then((data) => {
        setIsLoading(false);
        toast({
          variant: 'success',
          title: t("project_toast_add_success"),
       });
       props.setNewProject(false)
          navigate(`/projects`);

      }).catch((error) => {
        if (error.status === 400) {
          toast({
            variant: 'error',
            title: t("error_title"),
            description: t("save_project_toast_error_message", {
              projectName,
              advertiserName,
            }),
          });
        }
        console.log({error})
        setIsLoading(false);        
      });
  };
  
  return (
    <div css={cardStyles}>
      <div css={titleStyles}>{t("project_newProject_title")}</div>

      <form css={formStyles}>
        <div css={inputRowStyles}>
          <AdvertiserSearch
            setAdvertiser={(advertiser) => setAdvertiserName(advertiser)}
            focuseNext={setFocuseAdver}
            pageName="NewProject"
            advertiserLable={t("alertDialog_add_project_label_advertiser")}
          />
          <Input
            label={t("project_newProject_label_project")}
            placeholder={t("project_newProject_placeHolder_project")}
            onChange={(ev) => setProjectName(ev.target.value)}
            autoComplete="off"
            ref={inputRef}
            id="Projectname"
            required
          />
        </div>
        <div css={buttonContainerStyles}>
          <div css={compulsoryStyles}>{t("core_compulsory_field")}</div>
          <Button
            name="projectSave"
            variant={"brand"}
            icon={isLoading ? "" : "send"}
            color={theme.colors.primary}
            disabled={advertiserName.trim() === "" || projectName.trim() === "" || isLoading}
            onClick={onCreateProject}
            className="brandPrimaryBtn"
            aria-label={t("project_newProject_submit_button")}
          >
            {isLoading ? (
              <ProgressCircle size="xxs" color={theme.colors.primary} />
            ) : (
              t("project_newProject_submit_button")
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
